import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from "firebase/firestore";
import type { PickupLocation } from "../transporter/transporter";
import {
  Contact,
  ContactActivity,
  ContactActivityType,
  CrmActivityDomain,
  ContactOwnerId,
  ContactStage,
  ContactStageEntry,
  MarketingEmailStatus,
  normalizeContactBirthday,
  normalizeContactUserId,
} from "../leads/contacts";
import {
  getCurrentContactStage,
  normalizeContactStageHistory,
} from "../leads/contactStageHistory";

const VALID_STAGES: ContactStage[] = [
  "prospect",
  "opportunity",
  "customer",
  "unqualified",
];

const VALID_ACTIVITY_TYPES: ContactActivityType[] = [
  "call",
  "sms",
  "email",
  "note",
  "task",
  "lead",
  "shipping_repairs",
];

const parseActivityDomain = (v: unknown): CrmActivityDomain | undefined =>
  v === "inventory" || v === "sales" || v === "marketing" ? v : undefined;

const convertTimestamp = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (timestamp instanceof Date) return Number.isNaN(timestamp.getTime()) ? null : timestamp;
  if (typeof timestamp === "string") {
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const serializeDate = (d: Date): string =>
  d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : new Date().toISOString();

const serializeDateNullable = (d: Date | null | undefined): string | null => {
  if (!d) return null;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const cleanPickupLocation = (raw: Partial<PickupLocation> | undefined): PickupLocation | undefined => {
  if (!raw) return undefined;
  const address = raw.address?.trim() ?? "";
  const city = raw.city?.trim() ?? "";
  const state = raw.state?.trim() ?? "";
  const zip = raw.zip?.trim() ?? "";
  const contact_name = raw.contact_name?.trim();
  const contact_phone = raw.contact_phone?.trim();
  const notes = raw.notes?.trim();
  const company_name = raw.company_name?.trim();
  const secondary_contact_name = raw.secondary_contact_name?.trim();
  const secondary_contact_phone = raw.secondary_contact_phone?.trim();
  if (!address && !city && !state && !zip && !contact_name && !contact_phone && !notes) {
    return undefined;
  }
  const out: PickupLocation = { address, city, state, zip };
  if (contact_name) out.contact_name = contact_name;
  if (contact_phone) out.contact_phone = contact_phone;
  if (notes) out.notes = notes;
  if (company_name) out.company_name = company_name;
  if (secondary_contact_name) out.secondary_contact_name = secondary_contact_name;
  if (secondary_contact_phone) out.secondary_contact_phone = secondary_contact_phone;
  return out;
};

const parseOwnerId = (v: unknown): ContactOwnerId => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") return v;
  return null;
};

const parseStage = (v: unknown): ContactStage =>
  typeof v === "string" && (VALID_STAGES as string[]).includes(v) ? (v as ContactStage) : "prospect";

const parseMarketingEmailStatus = (v: unknown): MarketingEmailStatus =>
  v === "unsubscribed" ? "unsubscribed" : "subscribed";

const parseMarketingEmailSuppressed = (v: unknown): boolean => v === true;

const parseStageHistory = (raw: unknown): ContactStageEntry[] => {
  if (!Array.isArray(raw)) return [];
  const out: ContactStageEntry[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const stage = parseStage(e.stage);
    const changedAt = convertTimestamp(e.changedAt);
    if (!changedAt) continue;
    const changedByUserId =
      typeof e.changedByUserId === "string" && e.changedByUserId
        ? e.changedByUserId
        : undefined;
    out.push({
      stage,
      changedAt,
      ...(changedByUserId ? { changedByUserId } : {}),
    });
  }
  return out;
};

const serializeStageHistory = (history: ContactStageEntry[] | undefined) => {
  if (!Array.isArray(history)) return [];
  return history
    .map((entry) => {
      const stage = parseStage(entry?.stage);
      const changedAt =
        entry?.changedAt instanceof Date && !Number.isNaN(entry.changedAt.getTime())
          ? entry.changedAt.toISOString()
          : new Date().toISOString();
      return {
        stage,
        changedAt,
        ...(entry.changedByUserId ? { changedByUserId: entry.changedByUserId } : {}),
      };
    })
    .filter((v) => v !== null);
};

const parseActivityType = (v: unknown): ContactActivityType | null =>
  typeof v === "string" && (VALID_ACTIVITY_TYPES as string[]).includes(v)
    ? (v as ContactActivityType)
    : null;

const parseActivities = (raw: unknown): ContactActivity[] => {
  if (!Array.isArray(raw)) return [];
  const out: ContactActivity[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const type = parseActivityType(e.type);
    if (!type) continue;
    const createdAt = convertTimestamp(e.createdAt);
    if (!createdAt) continue;
    const note = typeof e.note === "string" ? e.note : "";
    const createdByUserId =
      typeof e.createdByUserId === "string" ? e.createdByUserId : "";
    const taskId = typeof e.taskId === "string" && e.taskId ? e.taskId : undefined;
    const leadId = typeof e.leadId === "string" && e.leadId ? e.leadId : undefined;
    const campaignId =
      typeof e.campaignId === "string" && e.campaignId ? e.campaignId : undefined;
    const domain = parseActivityDomain(e.domain);
    const activity: ContactActivity = {
      type,
      note,
      createdAt,
      createdByUserId,
      ...(taskId ? { taskId } : {}),
      ...(leadId ? { leadId } : {}),
      ...(campaignId ? { campaignId } : {}),
      ...(domain ? { domain } : {}),
    };
    out.push(activity);
  }
  return out;
};

const serializeActivities = (activities: ContactActivity[] | undefined) => {
  if (!Array.isArray(activities)) return [];
  return activities
    .map((a) => {
      const type = parseActivityType(a?.type);
      if (!type) return null;
      const createdAt =
        a?.createdAt instanceof Date && !Number.isNaN(a.createdAt.getTime())
          ? a.createdAt.toISOString()
          : new Date().toISOString();
      const domain = parseActivityDomain(a.domain);
      return {
        type,
        note: typeof a.note === "string" ? a.note : "",
        ...(a.taskId ? { taskId: a.taskId } : {}),
        ...(a.leadId ? { leadId: a.leadId } : {}),
        ...(a.campaignId ? { campaignId: a.campaignId } : {}),
        ...(domain ? { domain } : {}),
        createdAt,
        createdByUserId:
          typeof a.createdByUserId === "string" ? a.createdByUserId : "",
      };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null);
};

const parseStringArray = (v: unknown): string[] => {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const entry of v) {
    if (typeof entry === "string" && entry && !out.includes(entry)) {
      out.push(entry);
    }
  }
  return out;
};

export const contactConverter: FirestoreDataConverter<Contact> = {
  toFirestore(contact: Contact) {
    const { id: _id, ...rest } = contact;
    const dropoffLocation = cleanPickupLocation(rest.dropoffLocation);
    const createdAt =
      rest.createdAt instanceof Date && !Number.isNaN(rest.createdAt.getTime())
        ? rest.createdAt
        : new Date();
    const stageHistory = normalizeContactStageHistory(
      rest.stageHistory,
      parseStage(rest.stage),
      createdAt,
    );
    const stage = getCurrentContactStage(stageHistory, parseStage(rest.stage));
    const inventoryStageHistory = normalizeContactStageHistory(
      rest.inventoryStageHistory,
      parseStage(rest.inventoryStage ?? "prospect"),
      createdAt,
    );
    const inventoryStage = getCurrentContactStage(
      inventoryStageHistory,
      parseStage(rest.inventoryStage ?? "prospect"),
    );
    const userId = normalizeContactUserId(rest.userId);
    const birthday = normalizeContactBirthday(rest.birthday);
    const driverLicensePath =
      typeof rest.driver_license_front_storage_path === "string"
        ? rest.driver_license_front_storage_path.trim()
        : "";
    return {
      name: rest.name?.trim() ?? "",
      ...(rest.email?.trim() ? { email: rest.email.trim() } : {}),
      ...(rest.secondaryEmail?.trim()
        ? { secondaryEmail: rest.secondaryEmail.trim() }
        : {}),
      ...(rest.phone?.trim() ? { phone: rest.phone.trim() } : {}),
      ...(birthday ? { birthday } : {}),
      ownerId: rest.ownerId === undefined ? null : rest.ownerId,
      inventoryOwnerId:
        rest.inventoryOwnerId === undefined ? null : rest.inventoryOwnerId,
      stage,
      stageHistory: serializeStageHistory(stageHistory),
      inventoryStage,
      inventoryStageHistory: serializeStageHistory(inventoryStageHistory),
      ...(dropoffLocation ? { dropoffLocation } : {}),
      marketingEmailStatus: parseMarketingEmailStatus(rest.marketingEmailStatus),
      marketingEmailSuppressed: parseMarketingEmailSuppressed(rest.marketingEmailSuppressed),
      lastActivityAt: serializeDateNullable(rest.lastActivityAt ?? null),
      leadIds: Array.isArray(rest.leadIds) ? [...rest.leadIds] : [],
      activities: serializeActivities(rest.activities),
      ...(userId ? { userId } : {}),
      ...(driverLicensePath ? { driver_license_front_storage_path: driverLicensePath } : {}),
      createdAt: serializeDate(rest.createdAt),
      updatedAt: serializeDate(rest.updatedAt),
      createdByUserId: rest.createdByUserId ?? "",
    };
  },

  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): Contact {
    const data = snapshot.data(options) ?? {};
    const rawDrop = data.dropoffLocation as Partial<PickupLocation> | undefined;
    const dropoffLocation = cleanPickupLocation(rawDrop);

    const emailRaw = typeof data.email === "string" ? data.email.trim() : "";
    const secondaryEmailRaw =
      typeof data.secondaryEmail === "string" ? data.secondaryEmail.trim() : "";
    const phoneRaw = typeof data.phone === "string" ? data.phone.trim() : "";
    const birthdayRaw = normalizeContactBirthday(data.birthday);
    const userIdRaw = normalizeContactUserId(data.userId);
    const driverLicensePathRaw =
      typeof data.driver_license_front_storage_path === "string"
        ? data.driver_license_front_storage_path.trim()
        : "";

    const createdAt = convertTimestamp(data.createdAt) || new Date();
    const parsedStage = parseStage(data.stage);
    const stageHistory = normalizeContactStageHistory(
      parseStageHistory(data.stageHistory),
      parsedStage,
      createdAt,
    );
    const stage = getCurrentContactStage(stageHistory, parsedStage);

    // Legacy contacts have no inventory stage — default to prospect.
    const parsedInventoryStage = parseStage(data.inventoryStage ?? "prospect");
    const inventoryStageHistory = normalizeContactStageHistory(
      parseStageHistory(data.inventoryStageHistory),
      parsedInventoryStage,
      createdAt,
    );
    const inventoryStage = getCurrentContactStage(
      inventoryStageHistory,
      parsedInventoryStage,
    );

    return {
      id: snapshot.id,
      name: typeof data.name === "string" ? data.name : "",
      ...(emailRaw ? { email: emailRaw } : {}),
      ...(secondaryEmailRaw ? { secondaryEmail: secondaryEmailRaw } : {}),
      ...(phoneRaw ? { phone: phoneRaw } : {}),
      ...(birthdayRaw ? { birthday: birthdayRaw } : {}),
      ownerId: parseOwnerId(data.ownerId),
      inventoryOwnerId: parseOwnerId(data.inventoryOwnerId),
      stage,
      stageHistory,
      inventoryStage,
      inventoryStageHistory,
      ...(dropoffLocation ? { dropoffLocation } : {}),
      marketingEmailStatus: parseMarketingEmailStatus(data.marketingEmailStatus),
      marketingEmailSuppressed: parseMarketingEmailSuppressed(data.marketingEmailSuppressed),
      lastActivityAt: convertTimestamp(data.lastActivityAt),
      leadIds: parseStringArray(data.leadIds),
      activities: parseActivities(data.activities),
      ...(userIdRaw ? { userId: userIdRaw } : {}),
      ...(driverLicensePathRaw ? { driver_license_front_storage_path: driverLicensePathRaw } : {}),
      createdAt,
      updatedAt: convertTimestamp(data.updatedAt) || new Date(),
      createdByUserId: typeof data.createdByUserId === "string" ? data.createdByUserId : "",
    };
  },
};
